import React, { createContext, useState, useContext, useEffect } from 'react';
import { supabase } from './supabaseClient';

// ══════════════════════════════════════════════════════════════
// PEFEP Site — AUTH CONTEXT v2 (Migration Supabase)
// ══════════════════════════════════════════════════════════════
// Compatible avec l'API existante :
// - useAuth() retourne { user, login, logout, loading }
// - user.type est utilisé pour RouteProtegee (garde compatibilité)
// ══════════════════════════════════════════════════════════════

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // ─────────────────────────────────────────────────────────────
  // Charger le profil complet depuis la table `clients`
  // ─────────────────────────────────────────────────────────────
  const chargerProfil = async (authUser) => {
    if (!authUser) return null;

    try {
      const { data: profil, error } = await supabase
        .from('clients')
        .select('*')
        .eq('id', authUser.id)
        .single();

      if (error || !profil) {
        console.warn('Profil client non trouvé pour', authUser.email);
        return null;
      }

      // ✅ Construction de l'objet user COMPATIBLE avec l'ancien code
      // On garde user.type pour RouteProtegee (mappé depuis type_compte)
      return {
        id: profil.id,
        email: profil.email,
        nom: profil.nom,
        prenom: profil.prenom,
        telephone: profil.telephone,
        ville: profil.ville,
        type_compte: profil.type_compte, // 'simple' ou 'dev'
        type: profil.type_compte === 'dev' ? 'developpeur' : 'simple', // ✅ Compatible RouteProtegee
        plan_dev: profil.plan_dev,
        code_parrainage: profil.code_parrainage,
        parraine_par: profil.parraine_par,
        role: profil.role, // 👑 Nouveau : rôle admin/client
        created_at: profil.created_at,
      };
    } catch (err) {
      console.error('Erreur chargement profil :', err);
      return null;
    }
  };

  // ─────────────────────────────────────────────────────────────
  // Au démarrage : vérifier si l'utilisateur a une session active
  // ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const initSession = async () => {
      try {
        // Récupérer la session Supabase
        const { data: { session }, error } = await supabase.auth.getSession();

        if (error) {
          console.error('Erreur session :', error);
          setLoading(false);
          return;
        }

        if (session?.user) {
          const profil = await chargerProfil(session.user);
          setUser(profil);
        }
      } catch (err) {
        console.error('Erreur initialisation session :', err);
      } finally {
        setLoading(false);
      }
    };

    initSession();

    // ─────────────────────────────────────────────────────────────
    // Écouter les changements d'authentification en temps réel
    // ─────────────────────────────────────────────────────────────
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_IN' && session?.user) {
        const profil = await chargerProfil(session.user);
        setUser(profil);
      } else if (event === 'SIGNED_OUT') {
        setUser(null);
      }
    });

    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  // ─────────────────────────────────────────────────────────────
  // login() — Compatible avec l'ancienne API
  // Accepte soit un authUser Supabase, soit rien (auto-refresh)
  // ─────────────────────────────────────────────────────────────
  const login = async (authUser) => {
    if (authUser) {
      const profil = await chargerProfil(authUser);
      setUser(profil);
    } else {
      // Rafraîchir depuis la session actuelle
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        const profil = await chargerProfil(session.user);
        setUser(profil);
      }
    }
  };

  // ─────────────────────────────────────────────────────────────
  // logout() — Compatible avec l'ancienne API
  // ─────────────────────────────────────────────────────────────
  const logout = async () => {
    await supabase.auth.signOut();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading }}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
